// web/src/lib/resultCache.ts
// Refresh survival WITHOUT server persistence: a single-slot IndexedDB cache of the last
// GenerationResult, on the tester's OWN device. The raw bytes (audio base64, source File,
// deck raster Blobs) structured-clone into IDB; the artifact's blob: URLs die with the
// document, so rehydrateResult rebuilds them from those bytes on load. Overwritten by the
// next generation, cleared by flip/clear — the "no data retention on our side" posture is
// unchanged (nothing leaves the browser).
//
// Every path degrades gracefully (private mode, jsdom, quota errors): save/clear resolve
// silently, load resolves null — worst case is exactly today's behavior.
import type { GenerationResult } from "./runGeneration";
import type { CreateRequest } from "./createFlow";
import { base64ToObjectUrl } from "./blobUrl";

const DB_NAME = "groast-session";
const STORE = "generation";
const KEY = "last";
// Second slot: the ORIGINAL CreateRequest (source File + settings), so Flip still works
// after a refresh restores the result. Stored/loaded verbatim — nothing in it holds a
// blob: URL (Processing derives its cover URL from coverBlob itself), so no rehydration.
const REQUEST_KEY = "lastRequest";

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => { req.result.createObjectStore(STORE); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Rebuild the artifact's (dead) blob: URLs from the cached raw bytes. Pure given inputs. */
export function rehydrateResult(stored: GenerationResult): GenerationResult {
  const { artifact, saveInputs } = stored;
  // synthesizeSpeech/voiceDeck audio is WAV (mirrors saveRoast's base64ToBlob mime).
  const audioUrl = base64ToObjectUrl(saveInputs.audioBase64, "audio/wav");

  if (saveInputs.source === "photo") {
    return {
      ...stored,
      artifact: { ...artifact, audioUrl, imageUrl: URL.createObjectURL(saveInputs.imageFile) },
    };
  }

  const keyBySlide = new Map(saveInputs.keyRasters.map((r) => [r.slideIndex, r]));
  const fillerBySlide = new Map(saveInputs.fillerRasters.map((r) => [r.slideIndex, r.blob]));
  return {
    ...stored,
    artifact: {
      ...artifact,
      audioUrl,
      keySlides: artifact.keySlides?.map((k) => {
        const r = keyBySlide.get(k.slideIndex);
        return {
          ...k,
          imageUrl: r ? URL.createObjectURL(r.blob) : k.imageUrl,
          ...(r?.doodleBlob ? { doodleImageUrl: URL.createObjectURL(r.doodleBlob) } : {}),
        };
      }),
      fillerImages: artifact.fillerImages?.map((f) => {
        const b = fillerBySlide.get(f.slideIndex);
        return { ...f, imageUrl: b ? URL.createObjectURL(b) : f.imageUrl };
      }),
    },
  };
}

async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onabort = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
  db.close();
}

async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  const value = await new Promise<T | null>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  db.close();
  return value;
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onabort = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
  db.close();
}

export function saveResultCache(result: GenerationResult): Promise<void> {
  return idbPut(KEY, result);
}

export async function loadResultCache(): Promise<GenerationResult | null> {
  const stored = await idbGet<GenerationResult>(KEY);
  if (!stored) return null;
  try {
    return rehydrateResult(stored);
  } catch (e) {
    console.warn("[resultCache] rehydrate failed — treating as empty", e);
    return null;
  }
}

export function clearResultCache(): Promise<void> {
  return idbDelete(KEY);
}

export function saveRequestCache(req: CreateRequest): Promise<void> {
  return idbPut(REQUEST_KEY, req);
}

export function loadRequestCache(): Promise<CreateRequest | null> {
  return idbGet<CreateRequest>(REQUEST_KEY);
}

export function clearRequestCache(): Promise<void> {
  return idbDelete(REQUEST_KEY);
}
